/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the `.vqlist` question outline shown in the workspace list column.
 * @state-owner activities UI store owns the selected question
 * @allowed-imports React; same-module store; shared components, demo constants and hooks
 * @forbidden direct API client; navigation decisions
 * @public-api via modules/activities/index.ts
 * @side-effects scrolls the selected question into view
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { StatusIcon } from '../../../shared/components/StatusIcon';
import { QUESTIONS } from '../../../shared/lib/demo-data';
import type { ActivityRecord } from '../../../shared/lib/model';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useActivityUiStore } from '../store/activity-ui-store';

/** The branch that renders an activity's questions. */
export function ActivityOutline({ activity }: { activity: ActivityRecord }) {
  const t = useTranslator();
  const selectedQuestion = useActivityUiStore((state) => state.selectedQuestion);
  const setSelectedQuestion = useActivityUiStore((state) => state.setSelectedQuestion);

  const jumpTo = (index: number) => {
    setSelectedQuestion(index);
    window.setTimeout(() => {
      document.getElementById(`q${index}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 30);
  };

  return (
    <div className="vqlist">
      {QUESTIONS.map((question, index) => (
        <button
          className={`vqc ${index === selectedQuestion ? 'on' : ''} ${
            activity.reviewed[index] ? 'ok' : 'warn'
          }`}
          key={question.text}
          type="button"
          onClick={() => jumpTo(index)}
        >
          <span className="vqcn">{index + 1}</span>
          <span className="vqct">
            <b>{question.text}</b>
            <span>
              {`${activity.reviewed[index] ? t('已核准') : t('待核准')} · ${t(question.concept)}`}
            </span>
          </span>
          <span className="vqcs">
            <StatusIcon glyph={activity.reviewed[index] ? 'done' : 'warn'} />
          </span>
        </button>
      ))}
    </div>
  );
}

/** The list-column heading the outline is shown under. */
export function activityOutlineSubtitle(activity: ActivityRecord, t: (value: string) => string) {
  const pending = activity.reviewed.filter((value) => !value).length;
  return `${QUESTIONS.length} ${t('題')}${
    pending ? ` · ${pending} ${t('題待核准')}` : ` · ${t('全部已核准')}`
  }`;
}
